import React, { useState } from 'react'
import { Button, Flex, Form, Input, InputNumber, Select, Space } from 'antd'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect } from 'react-redux'
import { useParams } from 'react-router'
import Text from 'antd/es/typography/Text'
import Access, { useAssignmentAccess } from '../access/Access'
import { Perm, State } from '../../../../constants/assignment-permissions'
import { isNil } from 'lodash'
import ModalWithEditor from '../file-drawer/modal-with-editor/ModalWithEditor'
import TextEditor from '../../../../components/common/text-editor/TextEditor'
import QuillRenderer from '../../../../components/common/text-editor/QuillRenderer'
import ConceptualLabelSelect from '../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import { useAutoFocusFirstInput } from '../../../../../hooks/useAutoFocusFirstInput'
import PatternIcon from '../../../../components/common/PatternIcon'
import {
    getPatternWarningLevelColor,
    getPatternWarningLevelName,
    PatternWarningLevels,
} from '../../../../constants/pattern-warning-levels'
import { ContentRules } from '../access/access-rules/assignment-rules'
import { GROUP_NAME_LIMIT } from '../../../../constants/input-limits'

const EditClusterFooter = ({ isWaitingForClusterSave, id }) => {
    return (
        <>
            <Button type='primary' htmlType='submit' form={`editCluster-${id}`} loading={isWaitingForClusterSave}>
                Save
            </Button>
        </>
    )
}

const EditClusterModal = ({
    id,
    name,
    feedback,
    grade,
    warningLevel,
    showEditClusterModal,
    setShowEditClusterModal,
    saveClusterFeedback,
    conceptualLabels,
}) => {
    const { token, layerId } = useParams()
    const [form] = Form.useForm()
    useAutoFocusFirstInput()
    const [isWaitingForClusterSave, setIsWaitingForClusterSave] = useState(false)

    const { hasAccess: hasEditingPermissions } = useAssignmentAccess(ContentRules.EDIT_GRADINGS)

    const handleEditClusterModalClose = () => setShowEditClusterModal(false)

    const handleSaveCluster = async values => {
        setIsWaitingForClusterSave(true)
        await saveClusterFeedback(
            token,
            layerId,
            id,
            values.name,
            values.feedback,
            values.grade,
            values.conceptualLabels,
            values.warningLevel,
        )
        setIsWaitingForClusterSave(false)
        setShowEditClusterModal(false)
    }

    const { hasAccess: hasEditingFeedbackPermission } = useAssignmentAccess(ContentRules.EDIT_NAMINGS)

    const warningLevelOptions = Object.values(PatternWarningLevels).map(level => ({
        label: (
            <Space size={8}>
                <PatternIcon id={level} color={getPatternWarningLevelColor(level)} showId={false} />{' '}
                {getPatternWarningLevelName(level)}
            </Space>
        ),
        value: level,
    }))

    return (
        <ModalWithEditor
            title={hasEditingFeedbackPermission ? 'Edit Group' : 'View Group'}
            open={showEditClusterModal}
            footer={
                hasEditingFeedbackPermission ? (
                    <EditClusterFooter isWaitingForClusterSave={isWaitingForClusterSave} id={id} />
                ) : null
            }
            forceRender
            onCancel={handleEditClusterModalClose}
            width={1200}
        >
            <Form
                name={`editCluster-${id}`}
                onFinish={handleSaveCluster}
                form={form}
                autoComplete='off'
                layout='vertical'
                requiredMark={false}
            >
                <Form.Item
                    name='name'
                    rules={[
                        { required: true, message: 'Please, enter name for this group' },
                        { max: GROUP_NAME_LIMIT, message: `Group name cannot exceed ${GROUP_NAME_LIMIT} characters` }
                    ]}
                    label='Name'
                    initialValue={name}
                >
                    <Access
                        rules={ContentRules.EDIT_NAMINGS}
                        fallback={<Text>{name || 'This group has no name'}</Text>}
                    >
                        <Input maxLength={GROUP_NAME_LIMIT} showCount />
                    </Access>
                </Form.Item>
                <Form.Item name='feedback' initialValue={feedback}>
                    <Access
                        rules={ContentRules.EDIT_NAMINGS}
                        fallback={<QuillRenderer htmlContent={feedback || 'This group has no feedback'} />}
                    >
                        <TextEditor label='Feedback' />
                    </Access>
                </Form.Item>
                <Flex justify='space-between' gap={16}>
                    <Form.Item
                        name='grade'
                        label='Grade'
                        initialValue={grade ?? 0}
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <Access
                            rules={ContentRules.EDIT_GRADINGS}
                            fallback={<Text>{isNil(grade) ? 'This group has no grade' : `${grade}%`}</Text>}
                        >
                            <InputNumber addonAfter='%' style={{ width: 120 }} data-test="cluster-grade-input" />
                        </Access>
                    </Form.Item>
                    <Form.Item
                        name='conceptualLabels'
                        label='Conceptual Labels'
                        initialValue={conceptualLabels}
                        tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
                    >
                        <ConceptualLabelSelect disabled={!hasEditingPermissions} />
                    </Form.Item>
                </Flex>
                <Form.Item
                    label='Warning Level'
                    name='warningLevel'
                    rules={[{ required: true, message: 'Please, select warning level' }]}
                    initialValue={warningLevel}
                >
                    <Access
                        rules={ContentRules.EDIT_NAMINGS}
                        fallback={
                            <Space size={8}>
                                <PatternIcon id={warningLevel} color={getPatternWarningLevelColor(warningLevel)} />
                                {getPatternWarningLevelName(warningLevel)}
                            </Space>
                        }
                    >
                        <Select
                            options={warningLevelOptions}
                            popupMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentNode}
                            showSearch
                            optionFilterProp='label'
                        />
                    </Access>
                </Form.Item>
            </Form>
        </ModalWithEditor>
    )
}

const mapDispatch = dispatch => {
    return {
        saveClusterFeedback: (token, layerId, id, name, feedback, grade, conceptualLabels, warningLevel) => {
            return dispatch(
                actions.saveClusterFeedback(token, layerId, id, name, feedback, grade, conceptualLabels, warningLevel),
            )
        },
    }
}

export default connect(null, mapDispatch)(EditClusterModal)
